import { EmployeeDto } from "./employee.dto";
import { PaginatorInfo } from "./generic.dto";
import { LeavePackage } from "./leave-package.dto";

export const LEAVE_REQUEST_OPTIONS = {
    PENDING: "PENDING",
    APPROVED: "APPROVED",
    DECLINED: "DECLINED",
    CANCELLED: "CANCELLED"
}

const LEAVE_RESPONSE_OPTIONS = {
    APPROVED: "APPROVED",
    DECLINED: "DECLINED"
}

export type LEAVE_REQUEST_OPTIONS = keyof typeof LEAVE_REQUEST_OPTIONS;

export type LEAVE_RESPONSE_OPTIONS = keyof typeof LEAVE_RESPONSE_OPTIONS;

export type LeaveRequest = {
    id: number;
    employeeId: number;
    leavePackageId: number;
    startDate: string;
    returnDate: string;
    comment: string;
    status: LEAVE_REQUEST_OPTIONS;
    cancelledByEmployeeId: number | null;
    createdAt: string;
    modifiedAt: string | null;
    responseCompletedAt: string | null;
    cancelledAt: string | null;
}
  
export class CreateLeaveRequestDto {
    employeeId!: number;
    leaveTypeId!: number;
    startDate!: string;
    returnDate!: string;
    comment!: string;
}

export class UpdateLeaveRequestDto{
    leaveTypeId?: number;
    startDate?: string;
    returnDate?: string;
    comment?: string;
}

export enum LeaveRequestOrderBy {
    START_DATE_ASC = 'startDate:asc',
    START_DATE_DESC = 'startDate:desc',
    RETURN_DATE_ASC = 'returnDate:asc',
    RETURN_DATE_DESC = 'returnDate:desc',
    CREATED_AT_ASC = 'createdAt:asc',
    CREATED_AT_DESC = 'createdAt:desc',
    MODIFIED_AT_ASC = 'modifiedAt:asc',
    MODIFIED_AT_DESC = 'modifiedAt:desc',
}

export type REQUEST_QUERY_MODE ='SELF' | 'SUPERVISEES' | 'ALL';

export class QueryLeaveRequestDto {
    employeeId?: number;
    leavePackageId?: number;
    status?: LEAVE_REQUEST_OPTIONS;
    queryMode?: REQUEST_QUERY_MODE;
    'startDate.gte'?: string;
    'startDate.lte'?: string;
    'returnDate.gte'?: string;
    'returnDate.lte'?: string;
    page?: number = 1;
    limit?: number;
    orderBy: LeaveRequestOrderBy = LeaveRequestOrderBy.CREATED_AT_DESC;
}

export class LeaveResponseInputDto {
    action!: LEAVE_RESPONSE_OPTIONS;
    comment?: string;
}

export class CancelLeaveRequestDto {
    status!: LEAVE_REQUEST_OPTIONS;
    cancelledByEmployeeId!: number;
    cancelledAt!: string;
}

enum LEAVE_RESPONSE_TYPE {
    APPROVED = 'APPROVED',
    DECLINED = 'DECLINED',
    ADJUSTED = 'ADJUSTED'
  }

type LeaveResponse = {
    id: number;
    leaveRequestId: number;
    approvingEmployeeId: number;
    comment: string;
    responseType: LEAVE_RESPONSE_TYPE;
    createdAt: Date;
}

export interface LeaveRequestDto extends LeaveRequest {
    employee?: EmployeeDto,
    leavePackage?: LeavePackage,
    leaveResponses?: LeaveResponse[]
}

export interface LeaveRequestPaginator extends PaginatorInfo<LeaveRequestDto> {}

export const ADJUSTMENT_OPTIONS = {
    INCREASE: 'INCREASE',
    DECREASE: 'DECREASE'
  };
  
  export type ADJUSTMENT_OPTIONS =  keyof typeof ADJUSTMENT_OPTIONS;
  
  export class AdjustDaysDto {
    adjustment!: ADJUSTMENT_OPTIONS;
    count!: number;
    comment!: string;
  }

  export class ConvertLeavePlanToRequestDto {
    leavePlanId!: number;
  }

  export class UploadLeaveRequestViaSpreadsheetDto {
    rowNumber?: number;
    companyId!: number;
    employeeNumber!: string;
    leaveTypeCode!: string;
    startDate!: Date;
    returnDate!: Date;
    comment?: string;
    notifyApprovers!: 'Yes' | 'No';
  }
  
  export class UploadLeaveRequestCheckedRecords {
    employeeId!: number;
    leavePackageId!: number;
    numberOfDays!: number;
    approvalsRequired!: number;
    leaveTypeName?: string;
    notifyApprovers?: boolean;
  }
  
  export class UploadLeaveRequestResponse {
    successful!: {
      leaveRequestId?: number;
      rowNumber?: number;
      approversNotified?: boolean;
    }[];
    failed!: {
      rowNumber?: number;
      errors: {
        column: string;
        reason: string;
      }[]
    }[];
  }

  export class FilterLeaveRequestForExportDto {
  employeeId?: number;
  leavePackageId?: number;
  queryMode?: REQUEST_QUERY_MODE;
  status?: LEAVE_REQUEST_OPTIONS;
  'startDate.gte'?: string;
  'startDate.lte'?: string;
  'returnDate.gte'?: string;
  'returnDate.lte'?: string;
  'createdAt.gte'?: string;
  'createdAt.lte'?: string;
  page: number = 1;
  limit?: number;
  orderBy: LeaveRequestOrderBy = LeaveRequestOrderBy.CREATED_AT_DESC;
}

export class QueryLeaveRequestForReportDto {
  'startDate.gte'?: string;
  'startDate.lte'?: string;
  'returnDate.gte'?: string;
  'returnDate.lte'?: string;
  'createdAt.gte'?: string;
  'createdAt.lte'?: string;
  orderBy: LeaveRequestOrderBy = LeaveRequestOrderBy.CREATED_AT_DESC;
}

export class LeaveTakenReportObject {
  leaveType!: {
    id: number,
    code?: string,
    name?: string,
  };
  department!:LeaveTakenReportDepartmentObject[];
  numberOfDaysPerCompany!: number;
}

export interface LeaveTakenReportPaginationDto extends PaginatorInfo<LeaveTakenReportObject> {};

export class LeaveTakenReportDepartmentObject {
  id!: number;
  code!: string;
  name!: string;
  employees!: LeaveTakenReportEmployeeObject[];
  numberOfDaysPerDepartment!: number;
}

export class LeaveTakenReportEmployeeObject {
  id!: number;
  employeeNumber!: string;
  name!: string;
  numberOfDays!: number;
}

export class LeaveTakenWithPackageReportObject {
  id!: number;
  code?: string;
  name?: string;
  leavePackages!: EmployeeLeavePackageObject[];
}

export class EmployeeLeaveTakenReportObject {
  leaveType!: LeaveTakenWithPackageReportObject[];
  employee!: Omit <LeaveTakenReportEmployeeObject, 'numberOfDays'>;
}

export class EmployeeLeavePackageObject {
  id!: number;
  name!: string;
  code!: string;
  daysUsed!: number;
  daysApprovedButNotUsed!: number;
  daysPendingApproval!: number;
  daysAvailable!: number;
}

export class LeaveBalanceReportObject {
  employee!: {
    id: number;
    employeeNumber: string;
    name: string;
  };
  leaveTypes!: LeaveBalanceReportLeaveTypeObject[];
}
export interface LeaveBalanceReportPaginationDto extends PaginatorInfo<LeaveBalanceReportObject> {};

export class LeaveBalanceReportLeaveTypeObject {
  id!: number;
  code?: string;
  name?: string;
  leavePackages!: LeaveBalanceReportLeavePackageObject[];
}

export class LeaveBalanceReportLeavePackageObject {
  id!: number;
  code?: string;
  name?: string;
  remainingLeaveDays!: number;
}

export class ExportLeaveRequestQueryDto {
  employeeNumber?: boolean;
  leaveTypeCode?: boolean;
  startDate?: boolean;
  returnDate?: boolean;
  comment?: boolean;
  status?: boolean;
  cancelledByEmployeeNumber?: boolean;
  approvalsRequired?: boolean;
  numberOfDays?: boolean;
  queryMode?: REQUEST_QUERY_MODE;
  orderBy: LeaveRequestOrderBy = LeaveRequestOrderBy.CREATED_AT_DESC;
}